"use client";
/**
 * MermaidRenderer — renders a Mermaid diagram from a chart string.
 * Track C2.
 *
 * Supports fullscreen zoom modal with zoom controls and SVG/PNG download.
 * Only renders after streaming is complete (caller must not pass this
 * component while isStreaming === true).
 *
 * Theme-aware: re-renders diagram when the app switches between light/dark
 * mode (detected via .theme-light on <html>). Uses mermaid's "default"
 * theme for light mode and "dark" theme for dark mode.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import mermaid from "mermaid";
import { ZoomIn, ZoomOut, X, Maximize2, Download } from "lucide-react";

/** Returns true when the app is in light mode. */
function isLightMode(): boolean {
  if (typeof document === "undefined") return false;
  return document.documentElement.classList.contains("theme-light");
}

/** Returns the mermaid theme string for the current app theme. */
function getMermaidTheme(): "default" | "dark" {
  return isLightMode() ? "default" : "dark";
}

interface MermaidRendererProps {
  chart: string;
  id?:   string;
  /** Render with the light "default" mermaid theme regardless of app theme.
      Used by the print document so dark-mode diagrams stay readable on paper. */
  forceLightTheme?: boolean;
}

type RenderState = "idle" | "rendering" | "success" | "error";

export function MermaidRenderer({ chart, id, forceLightTheme = false }: MermaidRendererProps) {
  const containerRef  = useRef<HTMLDivElement>(null);
  const uniqueId      = useRef(id ?? `mermaid-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);
  const isMountedRef  = useRef(true);
  const renderCountRef = useRef(0);

  const [renderState, setRenderState] = useState<RenderState>("idle");
  const [isZoomed,    setIsZoomed]    = useState(false);
  const [zoomLevel,   setZoomLevel]   = useState(1);
  // Tracks current mermaid theme so theme changes trigger a re-render
  const [mermaidTheme, setMermaidTheme] = useState<"default" | "dark">(
    () => (forceLightTheme ? "default" : getMermaidTheme())
  );

  // Track mount state
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Watch for .theme-light toggling on <html> and flip mermaidTheme accordingly
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (forceLightTheme) return; // print output is always light

    const observer = new MutationObserver(() => {
      const next = getMermaidTheme();
      setMermaidTheme((prev) => (prev !== next ? next : prev));
    });

    observer.observe(document.documentElement, {
      attributes:      true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, [forceLightTheme]);

  // Render diagram when chart or theme changes
  const renderDiagram = useCallback(async () => {
    if (!containerRef.current || !isMountedRef.current) return;

    setRenderState("rendering");
    const thisRender = ++renderCountRef.current;

    try {
      containerRef.current.innerHTML = "";

      // Re-initialise mermaid with the current theme before each render
      mermaid.initialize({
        startOnLoad:   false,
        theme:         mermaidTheme,
        securityLevel: "loose",
        fontFamily:    "inherit",
      });

      // Validate syntax first
      await mermaid.parse(chart);

      // Use a unique id per render to avoid mermaid id collisions after re-renders
      const renderId = `${uniqueId.current}-${thisRender}`;
      const { svg }  = await mermaid.render(renderId, chart);

      if (isMountedRef.current && containerRef.current && renderCountRef.current === thisRender) {
        containerRef.current.innerHTML = svg;
        setRenderState("success");
      }
    } catch {
      if (isMountedRef.current && containerRef.current && renderCountRef.current === thisRender) {
        containerRef.current.innerHTML =
          `<div class="text-muted-foreground text-sm italic p-2">Invalid diagram syntax</div>`;
        setRenderState("error");
      }
    }
  }, [chart, mermaidTheme]);

  useEffect(() => {
    const timer = setTimeout(renderDiagram, 100);
    return () => clearTimeout(timer);
  }, [renderDiagram]);

  // ── Zoom handlers ────────────────────────────────────────────────────────

  const handleZoomIn    = () => setZoomLevel((z) => Math.min(z + 0.25, 3));
  const handleZoomOut   = () => setZoomLevel((z) => Math.max(z - 0.25, 0.5));
  const handleResetZoom = () => setZoomLevel(1);
  const handleOpen      = () => { setIsZoomed(true); setZoomLevel(1); };
  const handleClose     = () => { setIsZoomed(false); setZoomLevel(1); };

  // ── Download helpers ─────────────────────────────────────────────────────

  const handleDownloadSVG = () => {
    const svg = containerRef.current?.querySelector("svg");
    if (!svg) return;

    const cloned = svg.cloneNode(true) as SVGElement;
    const bbox   = svg.getBBox();
    const w      = bbox.width  || svg.clientWidth;
    const h      = bbox.height || svg.clientHeight;
    cloned.setAttribute("width",  w.toString());
    cloned.setAttribute("height", h.toString());

    const blob = new Blob([new XMLSerializer().serializeToString(cloned)], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href     = url;
    link.download = "diagram.svg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadPNG = () => {
    const svg = containerRef.current?.querySelector("svg");
    if (!svg) return;

    const cloned = svg.cloneNode(true) as SVGElement;
    const bbox   = svg.getBBox();
    const w      = bbox.width  || svg.clientWidth;
    const h      = bbox.height || svg.clientHeight;
    cloned.setAttribute("width",  w.toString());
    cloned.setAttribute("height", h.toString());

    const svgData    = new XMLSerializer().serializeToString(cloned);
    const svgDataUrl = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));

    const scale  = 2;
    const canvas = document.createElement("canvas");
    canvas.width  = w * scale;
    canvas.height = h * scale;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(scale, scale);

    const img    = new Image();
    img.onload   = () => {
      // Use a background that matches the current theme
      ctx.fillStyle = isLightMode() ? "#ffffff" : "#1e1e2e";
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url  = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href     = url;
        link.download = "diagram.png";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, "image/png");
    };
    img.src = svgDataUrl;
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <div className="relative group">
        <div
          ref={containerRef}
          className="mermaid-container my-4 flex justify-center overflow-x-auto"
          data-render-state={renderState}
        />

        {renderState === "success" && (
          <button
            onClick={handleOpen}
            title="Zoom diagram"
            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md shadow-md"
            style={{ background: "var(--card)", border: "1px solid var(--border)" }}
            aria-label="Zoom diagram"
          >
            <Maximize2 className="h-4 w-4" style={{ color: "var(--foreground)" }} />
          </button>
        )}
      </div>

      {/* Fullscreen zoom modal via portal */}
      {isZoomed &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
            onClick={handleClose}
          >
            <div
              className="relative rounded-lg shadow-2xl w-[95vw] h-[95vh] flex flex-col"
              style={{ background: "var(--card)", border: "1px solid var(--border)" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div
                className="flex items-center justify-between p-4 flex-shrink-0"
                style={{ borderBottom: "1px solid var(--border)" }}
              >
                <span className="text-sm font-medium" style={{ color: "var(--muted-foreground)" }}>
                  Zoom: {Math.round(zoomLevel * 100)}%
                </span>

                <div className="flex items-center gap-2">
                  <button
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= 0.5}
                    title="Zoom out"
                    className="p-1.5 rounded transition-colors disabled:opacity-40"
                    style={{ color: "var(--foreground)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--muted)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    aria-label="Zoom out"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </button>

                  <button
                    onClick={handleResetZoom}
                    title="Reset zoom"
                    className="px-2 py-1 rounded text-xs transition-colors"
                    style={{ color: "var(--foreground)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--muted)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    Reset
                  </button>

                  <button
                    onClick={handleZoomIn}
                    disabled={zoomLevel >= 3}
                    title="Zoom in"
                    className="p-1.5 rounded transition-colors disabled:opacity-40"
                    style={{ color: "var(--foreground)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--muted)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    aria-label="Zoom in"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </button>

                  <div className="w-px h-6 mx-1" style={{ background: "var(--border)" }} />

                  <button
                    onClick={handleDownloadSVG}
                    title="Download SVG"
                    className="flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors"
                    style={{ color: "var(--foreground)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--muted)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <Download className="h-3.5 w-3.5" />
                    SVG
                  </button>

                  <button
                    onClick={handleDownloadPNG}
                    title="Download PNG"
                    className="flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors"
                    style={{ color: "var(--foreground)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--muted)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <Download className="h-3.5 w-3.5" />
                    PNG
                  </button>

                  <div className="w-px h-6 mx-1" style={{ background: "var(--border)" }} />

                  <button
                    onClick={handleClose}
                    title="Close"
                    className="p-1.5 rounded transition-colors"
                    style={{ color: "var(--foreground)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--muted)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    aria-label="Close zoom modal"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Body — scrollable */}
              <div className="flex-1 overflow-auto p-8">
                <div
                  className="flex justify-center items-center min-h-full transition-transform duration-200"
                  style={{ transform: `scale(${zoomLevel})`, transformOrigin: "center top" }}
                  dangerouslySetInnerHTML={{ __html: containerRef.current?.innerHTML ?? "" }}
                />
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
